import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, MapPin, Navigation, RefreshCw, Truck, Users } from 'lucide-react';
import { CoverageMap, HeatStrip } from '../components/charts.jsx';
import { JobStatusBadge, LumperName, PageHead, PriorityFlag } from '../components/bits.jsx';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHead,
  EmptyState,
  Segmented,
  Stat,
  cx,
} from '../components/ui.jsx';
import { usePoll } from '../lib/hooks.js';
import { hours, num, relative, timeLabel, untilLabel } from '../lib/format.js';

export default function LiveCoverage() {
  const { data, loading, refresh } = usePoll('/dispatch/live', { ms: 45_000 });
  const { data: roster } = usePoll('/lumpers', { params: { status: 'active', available: 'true', limit: 40 }, ms: 90_000 });
  const [focus, setFocus] = useState(null);

  const jobs = data || [];

  const points = useMemo(
    () =>
      jobs
        .filter((job) => job.lat && job.lng)
        .map((job) => ({
          id: job.id,
          lat: job.lastLat || job.lat,
          lng: job.lastLng || job.lng,
          status: job.status,
          label: `${job.code} · ${job.siteName}`,
        })),
    [jobs],
  );

  const hourly = useMemo(() => {
    const buckets = Array.from({ length: 24 }, (_, hour) => ({ label: `${hour}:00`, value: 0 }));
    jobs.forEach((job) => {
      const hour = new Date(job.windowStart).getHours();
      buckets[hour].value += 1;
    });
    return buckets;
  }, [jobs]);

  const activeNow = jobs.filter((job) => ['checked_in', 'in_progress'].includes(job.status));
  const rolling = jobs.filter((job) => job.status === 'en_route');
  const free = roster || [];

  return (
    <>
      <PageHead
        title="Where&#39;s everyone"
        subtitle="See who is on the way, who is already unloading, and who is free to take the next job."
        icon={Activity}
        actions={
          <>
            <Segmented options={[{ label: 'Map' }, { label: 'Board' }]} value="Map" onChange={() => {}} />
            <Button variant="outline" size="sm" icon={RefreshCw} onClick={refresh}>Refresh</Button>
          </>
        }
      />

      <div className="grid grid-4 mb-20">
        <Stat label="Jobs not finished" icon={Truck} value={jobs.length} tone="amber" foot={`${jobs.filter((j) => !j.lumperName).length} with no lumper`} />
        <Stat label="Unloading right now" icon={MapPin} value={activeNow.length} tone="lime" foot="checked in or unloading" />
        <Stat label="On the way" icon={Navigation} value={rolling.length} tone="violet" foot="en route to a dock" />
        <Stat label="Free to take work" icon={Users} value={free.length} tone="blue" foot="active and unassigned" />
      </div>

      <div className="split-wide mb-20">
        <Card>
          <CardHead
            title="Where everybody is"
            subtitle="Pins use the last geotagged field event for jobs in flight"
            icon={MapPin}
          />
          <CardBody>
            <CoverageMap points={points} height={420} highlightId={focus} onSelect={(point) => setFocus(point.id)} />
          </CardBody>
        </Card>

        <div className="col gap-20">
          <Card>
            <CardHead title="Jobs starting each hour" subtitle="How the day is shaping up" />
            <CardBody>
              <HeatStrip cells={hourly} height={26} />
              <div className="row between dim mt-8" style={{ fontSize: 10 }}>
                <span>12a</span><span>6a</span><span>12p</span><span>6p</span><span>11p</span>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHead
              title="Free to take a job"
              subtitle={`${free.length} lumpers with nothing on the board`}
              icon={Users}
              actions={<Link to="/app/lumpers"><Button size="sm" variant="ghost">Roster</Button></Link>}
            />
            <CardBody className="col gap-10" style={{ maxHeight: 260, overflow: 'auto' }}>
              {free.slice(0, 12).map((lumper) => (
                <LumperName
                  key={lumper.id}
                  id={lumper.id}
                  name={`${lumper.firstName} ${lumper.lastName}`}
                  tone={lumper.photoColor}
                  subtitle={`${lumper.city || ''} ${lumper.state || ''} · ${Number(lumper.rating || 0).toFixed(1)}★ · ${lumper.jobsCompleted} jobs`}
                />
              ))}
              {!free.length ? <span className="dim">Nobody free — the whole roster is working.</span> : null}
            </CardBody>
          </Card>
        </div>
      </div>

      <Card>
        <CardHead title="What is happening now" subtitle="Live board with the last reported event" icon={Truck} />
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th className="narrow">Status</th>
                <th>Job</th>
                <th>Site</th>
                <th>Lumper</th>
                <th className="narrow">Window</th>
                <th className="narrow">Last event</th>
                <th className="right">Pay</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr key={job.id} className={cx('clickable', focus === job.id && 'selected')} onClick={() => setFocus(job.id)}>
                  <td className="narrow"><JobStatusBadge status={job.status} size="sm" /></td>
                  <td>
                    <div className="col gap-2">
                      <span className="row gap-6">
                        <Link to={`/app/jobs/${job.id}`} className="mono" style={{ fontSize: 'var(--fs-sm)' }}>{job.code}</Link>
                        <PriorityFlag priority={job.priority} />
                      </span>
                      <span className="dim truncate" style={{ fontSize: 'var(--fs-xs)' }}>{job.customerName}</span>
                    </div>
                  </td>
                  <td>
                    <div className="col gap-2">
                      <span className="truncate" style={{ fontSize: 'var(--fs-sm)' }}>{job.siteName}</span>
                      <span className="dimmer" style={{ fontSize: 'var(--fs-xs)' }}>{job.city}, {job.state}</span>
                    </div>
                  </td>
                  <td>
                    {job.lumperName ? (
                      <LumperName
                        id={job.lumperId}
                        name={job.lumperName}
                        tone={job.lumperPhotoColor}
                        subtitle={job.lumperTier !== 'standard' ? job.lumperTier.replace('_', ' ') : undefined}
                      />
                    ) : (
                      <Badge tone="amber" dot>Needs a lumper</Badge>
                    )}
                  </td>
                  <td className="narrow">
                    <div className="col gap-2">
                      <span className="num" style={{ fontSize: 'var(--fs-sm)' }}>{timeLabel(job.windowStart)}</span>
                      <span className="dimmer" style={{ fontSize: 'var(--fs-xs)' }}>{untilLabel(job.windowStart)}</span>
                    </div>
                  </td>
                  <td className="narrow">
                    <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{relative(job.lastEventAt)}</span>
                    <div className="dimmer" style={{ fontSize: 10 }}>{hours(job.estimatedHours)} est</div>
                  </td>
                  <td className="right num">{num(job.lumperPay, 0)}</td>
                </tr>
              ))}
              {!jobs.length && !loading ? (
                <tr>
                  <td colSpan={7}>
                    <EmptyState icon={Truck} title="Nothing in flight">Nothing open right now.</EmptyState>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
